import React, { useState, useEffect, useRef, useCallback } from 'react';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import './ProductForm.css';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';

const EMPTY_FORM = {
  tenantCode: '',
  productName: '',
  productDescription: '',
  catReference: '',
  basePrice: '0.00',
  membershipPrice: '0.00',
  festivalPrice: '0.00',
  seasonal: 'regular',
  status: 'Published',
  printerName: '',
  printerReference: '',
};

function ProductForm({ onNavigate }) {
  const { restaurants, selectedRestaurant } = useRestaurant() || {};
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageInputRef = useRef(null);

   // 打印机列表
  const [prinList, setPrinList] = useState([]);


  const fetchCategories = useCallback(async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/catalog/categories/options?tenantReference=${selectedRestaurant.reference}`), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        setCategories(data.data);
        if (data.data.length > 0) {
          setForm(prev => ({ ...prev, catReference: data.data[0].reference }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  }, [selectedRestaurant]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedRestaurant) {
      setForm(prev => ({ ...prev, tenantCode: selectedRestaurant.code }));
    }
  }, [selectedRestaurant]);

  const DESC_MAX = 50;

  const NAME_MAX = 50;

  const validate = () => {
    const errors = {};

    if (!form.productName.trim()) {
      errors.productName = 'Product name is required.';
    } else if (form.productName.trim().length > NAME_MAX) {
      errors.productName = `Product name cannot exceed ${NAME_MAX} characters.`;
    }

    if (!form.productDescription.trim()) {
      errors.productDescription = 'Product description is required.';
    } else if (form.productDescription.trim().length > DESC_MAX) {
      errors.productDescription = `Description cannot exceed ${DESC_MAX} characters.`;
    }

    if (!form.catReference) {
      errors.catReference = 'Please select a category.';
    }

    if (!form.printerReference) {
      errors.printerReference = 'Please select a printer.';
    }

    if (form.basePrice === '' || isNaN(parseFloat(form.basePrice)) || parseFloat(form.basePrice) < 0) {
      errors.basePrice = 'Please enter a valid base price.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  
  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setImageFile(file);
  };

  // 查询打印机
  const getPrintIn = async (tenantRef) => {
    if (!tenantRef) return;
    try {
      const res = await fetch(
        buildApiUrl(`/setting/printers/by-tenant?tenantReference=${tenantRef}`),
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.code === 200) {
        setPrinList(data.data);
      }
    } catch (err) {
      notify(err, 'error');
      console.error("Failed to fetch printers", err);
    }
  };

  useEffect(() => {
    getPrintIn(selectedRestaurant?.reference);
  }, [selectedRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  // 打印机选择
  const handleFormChangePrint = (e) => {
    const selectedReference = e.target.value;
    const selectedPrinter = prinList.find(p => p.reference === selectedReference);
    setForm(prev => ({
      ...prev,
      printerReference: selectedReference,
      printerName: selectedPrinter?.printerName || '',
    }));
    setFormErrors(prev => ({ ...prev, printerReference: undefined }));
  };

  // 选中分类后自动关联该分类的打印机
  useEffect(() => {
    if (!form.catReference || prinList.length === 0) {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
      return;
    }
    const selectedCat = categories.find(c => c.reference === form.catReference);
    if (selectedCat?.printerReference) {
      const matchedPrinter = prinList.find(p => p.reference === selectedCat.printerReference);
      if (matchedPrinter) {
        setForm(prev => ({
          ...prev,
          printerReference: matchedPrinter.reference,
          printerName: matchedPrinter.printerName,
        }));
      }
    } else {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
    }
  }, [form.catReference, categories, prinList]); // eslint-disable-line react-hooks/exhaustive-deps



  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const product = {
        tenantReference: selectedRestaurant?.reference,
        tenantCode: form.tenantCode,
        catReference: form.catReference,
        itemType: 0,
        itemName: form.productName.trim(),
        itemDesc: form.productDescription.trim(),
        basePrice: parseFloat(form.basePrice) || 0,
        memberPrice: parseFloat(form.membershipPrice) || 0,
        festivalPrice: parseFloat(form.festivalPrice) || 0,
        seasonal: form.seasonal === 'seasonal' ? 1 : 0,
        status: form.status === 'Published' ? 1 : 0,
        printerReference: form.printerReference || null,
      };

      const formData = new FormData();
      formData.append('product', JSON.stringify(product));
      if (imageFile) formData.append('image', imageFile);

      const res = await fetch(buildApiUrl('/product/simple/add'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.code === 200) {
        notify('Product added successfully!', 'success');
        onNavigate('Product');
      } else {
        notify('Failed to add product: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Error adding product:', err);
      notify('Failed to add product: ' + err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (await confirm('Are you sure you want to cancel? All changes will be lost.')) {
      onNavigate('Product');
    }
  };

  const hasChanges = form.productName !== '' || form.productDescription !== '' || imageFile !== null;

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="pf-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item =>
            (
              <li key={item} className={item === 'Product' ? 'active' : ''} onClick={() => onNavigate(getRouteFromItem(item))}>
                <span className="nav-icon">{navIcons[item]}</span> {item}
              </li>
            )
          )}
        </ul>
      </nav>

      <main className="pf-main">
        <h1 className="pf-title">Add New Simple Product</h1>

        <div className="pf-card">
          {/* Row: Product Name + Categories */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Product Name <span className="pf-required">*</span></label>
              <input
                className={`pf-input${formErrors.productName ? ' pf-input-error' : ''}`}
                name="productName"
                value={form.productName}
                onChange={handleChange}
                placeholder="Enter product name"
              />
              {formErrors.productName
                ? <span className="pf-field-error">{formErrors.productName}</span>
                : <span className="pf-hint">A product name is required and recommended to be unique</span>
              }
            </div>
            <div className="pf-field">
              <label>Categories <span className="pf-required">*</span></label>
              <select className={`pf-select${formErrors.catReference ? ' pf-input-error' : ''}`} name="catReference" value={form.catReference} onChange={handleChange} disabled={categories.length === 0}>
                {categories.length === 0
                  ? <option value="" disabled style={{ color: '#aaa' }}>-- no categories added. --</option>
                  : categories.map(c => (
                      <option key={c.reference} value={c.reference}>{c.catName}</option>
                    ))}
              </select>
              {formErrors.catReference && <span className="pf-field-error">{formErrors.catReference}</span>}
            </div>
          </div>

          {/* Product Description */}
          <div className="pf-field">
            <label>Product Description <span className="pf-required">*</span></label>
            <div className="pf-editor">
              <textarea
                className={`pf-textarea${formErrors.productDescription ? ' pf-input-error' : ''}`}
                name="productDescription"
                value={form.productDescription}
                onChange={handleChange}
                placeholder="Provide a detailed description of your product"
                maxLength={DESC_MAX + 1}
              />
            </div>
            {formErrors.productDescription && <span className="pf-field-error">{formErrors.productDescription}</span>}
          </div>

          {/* Upload Image */}
          <div className="pf-field">
            <button type="button" className="pf-upload-btn" onClick={() => imageInputRef.current.click()}>
              Upload Product Images
            </button>
            {imageFile && <span className="pf-hint" style={{ marginTop: 8 }}>{imageFile.name}</span>}
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
          </div>

          {/* Row: Base Price + Seasonal */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Base Price <span className="pf-required">*</span></label>
              <input className={`pf-input${formErrors.basePrice ? ' pf-input-error' : ''}`} name="basePrice" type="number" step="0.01" min="0" value={form.basePrice} onChange={handleChange} />
              {formErrors.basePrice && <span className="pf-field-error">{formErrors.basePrice}</span>}
            </div>
            <div className="pf-field">
              <label>Seasonal</label>
              <select className="pf-select" name="seasonal" value={form.seasonal} onChange={handleChange}>
                <option value="regular">regular</option>
                <option value="seasonal">seasonal</option>
              </select>
            </div>
          </div>

          {/* Membership Price + Festival Price */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Membership Price</label>
              <input className="pf-input" name="membershipPrice" type="number" step="0.01" min="0" value={form.membershipPrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Festival Price</label>
              <input className="pf-input" name="festivalPrice" type="number" step="0.01" min="0" value={form.festivalPrice} onChange={handleChange} />
            </div>
          </div>

          {/* Status + Printer */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Status</label>
              <select className="pf-select" name="status" value={form.status} onChange={handleChange}>
                <option>Published</option>
                <option>Unpublished</option>
              </select>
            </div>
            <div className="pf-field">
              <label>Printer <span className="pf-required">*</span></label>
              
              <select
                className={`pf-select${formErrors.printerReference ? ' pf-input-error' : ''}`}
                name="printerReference"
                value={form.printerReference || ''}
                onChange={handleFormChangePrint}
              >
                <option value="">-- Select Printer --</option>
                {prinList.map((printer) => (
                  <option key={printer.id} value={printer.reference}>
                    {printer.printerName}
                  </option>
                ))}
              </select>
              {formErrors.printerReference && <span className="pf-field-error">{formErrors.printerReference}</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pf-actions">
          <button className="pf-cancel-btn" onClick={handleCancel} disabled={isSubmitting}>Cancel</button>
          <button
            className={`pf-save-btn${hasChanges ? '' : ' pf-save-inactive'}`}
            onClick={handleSave}
            disabled={isSubmitting || !hasChanges}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  );
}

export default ProductForm;
